import i18next from '~/shared/libs/i18n/client-side';

export type GraphQLOperationType = 'query' | 'mutation' | 'subscription';

export type GraphQLErrorExtensions = Readonly<{
  code?: string;
  title?: string;
  exception?: Readonly<{
    title?: string;
  }>;
}>;

export type GraphQLErrorItem = Readonly<{
  message?: string;
  statusCode?: number;
  extensions?: GraphQLErrorExtensions;
}>;

export type GraphQLResponse<TData> = Readonly<{
  data?: TData;
  errors?: readonly GraphQLErrorItem[];
}>;

export type ParsedGraphQLOperation = Readonly<{
  opType?: GraphQLOperationType;
  opName?: string;
}>;

export type NormalizedGraphQLError = Readonly<{
  rawMessage: string;
  rawTitle: string;
  isSystemGraphQLError: boolean;
  code: string;
  statusCode?: number;
  isTechnical?: boolean;
}>;

export class FetchGraphQLError extends Error {
  public readonly title?: string;
  public readonly code?: string;
  public readonly isTechnical?: boolean;
  public readonly statusCode?: number;
  public readonly isSystemGraphQLError?: boolean;

  public constructor(params: {
    message: string;
    title?: string;
    code?: string;
    isTechnical?: boolean;
    statusCode?: number;
    isSystemGraphQLError?: boolean;
  }) {
    super(params.message);
    this.name = 'FetchGraphQLError';
    this.title = params.title;
    this.code = params.code;
    this.isTechnical = params.isTechnical;
    this.statusCode = params.statusCode;
    this.isSystemGraphQLError = params.isSystemGraphQLError;
  }
}

export function parseGraphQLOperation(query: string): ParsedGraphQLOperation {
  const match = /\b(query|mutation|subscription)\s+(\w+)/.exec(query);

  return match
    ? {
        opType: match[1] as GraphQLOperationType,
        opName: match[2],
      }
    : {};
}

export function normalizeGraphQLErrors(errors: readonly GraphQLErrorItem[]): NormalizedGraphQLError[] {
  return errors.map((errorItem) => {
    const rawMessage = errorItem.message || '';
    const rawTitle = errorItem.extensions?.title || errorItem?.title || errorItem.extensions?.exception?.title || '';

    const isSystemGraphQLError =
      !rawTitle &&
      (rawMessage.includes('Variable "$') ||
        rawMessage.includes('got invalid value') ||
        rawMessage.includes('Field "') ||
        rawMessage.includes('was not provided') ||
        rawMessage.includes('GraphQL validation') ||
        rawMessage.includes('Syntax Error'));

    return {
      rawMessage,
      rawTitle,
      isSystemGraphQLError,
      code: errorItem.extensions?.code || '',
      statusCode: errorItem.statusCode,
    };
  });
}

export function pickDisplayGraphQLError(
  normalizedErrors: readonly NormalizedGraphQLError[],
): NormalizedGraphQLError | null {
  if (!normalizedErrors.length) {
    return null;
  }

  const firstHumanError = normalizedErrors.find((item) => !item.isSystemGraphQLError);

  return firstHumanError ?? normalizedErrors[0];
}

export async function getLocalizedGraphQLError(params: { error: NormalizedGraphQLError }): Promise<{
  title: string;
  message: string;
}> {
  const { error } = params;
  const errorNs = 'error';

  if (!i18next.hasLoadedNamespace(errorNs)) {
    await i18next.loadNamespaces([errorNs]);
  }

  const tError = i18next.getFixedT(null, 'error');

  if (error.isSystemGraphQLError) {
    return {
      title: tError(($) => $.error.message['graphql-error'].title),
      message: tError(($) => $.error.message['graphql-error'].message),
    };
  }

  return {
    title: error.rawTitle || tError(($) => $.error.message['unknown-error'].title),
    message: error.rawMessage || tError(($) => $.error.message['unknown-error'].message),
  };
}

export async function getLocalizedHttpError(params: { status?: number | string }): Promise<{
  title: string;
  message: string;
}> {
  const { status } = params;
  const errorNs = 'error';
  const tError = i18next.getFixedT(null, 'error');

  if (!i18next.hasLoadedNamespace(errorNs)) {
    await i18next.loadNamespaces([errorNs]);
  }

  const title =
    typeof status === 'string'
      ? tError(($) => $.error.message['network'].title)
      : tError(($) => $.error.message['unknown-error'].title);

  const message = tError(($) => $.error.message['unknown-error'].message);

  return { title, message };
}

type SanitizableObject = Record<string, unknown>;

export type RumErrorSourceType = 'apollo' | 'rtk' | 'fetchGraphQL';

type RumErrorParams = Readonly<{
  source: RumErrorSourceType;
  operationName?: string;
  message: string;
  clientRequestId?: string;
  variables?: unknown;
  extra?: Record<string, unknown>;
}>;

type NormalizedApolloError = Readonly<{
  rawMessage: string;
  rawTitle: string;
  code: string;
  isSystemGraphQLError: boolean;
}>;

const sanitizeVariables = (variables: unknown): unknown => {
  try {
    if (!variables || typeof variables !== 'object') {
      return undefined;
    }

    const cloned = JSON.parse(JSON.stringify(variables)) as SanitizableObject;
    const redactKeys = ['password', 'token', 'secret', 'accessToken', 'refreshToken', 'id_token'];

    const walk = (obj: unknown) => {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      const record = obj as SanitizableObject;

      for (const key of Object.keys(record)) {
        const value = record[key];

        if (redactKeys.includes(key)) {
          record[key] = '[REDACTED]';
        } else if (value && typeof value === 'object') {
          walk(value);
        }
      }
    };

    walk(cloned);

    return cloned;
  } catch {
    return undefined;
  }
};

export const reportRumError = (params: RumErrorParams) => {
  if (typeof window === 'undefined') {
    return;
  }

  const rum = window.__pranaClubRum;

  if (!rum) {
    return;
  }

  const pageId = window.location.pathname;
  const errorAttributes = {
    source: params.source,
    operationName: params.operationName || 'unknown',
    pageId,
    clientRequestId: params.clientRequestId || '',
    variables: JSON.stringify(sanitizeVariables(params.variables) || {}),
    ...params.extra,
  };

  try {
    rum.recordError(
      new Error(
        `[${params.source.toUpperCase()}:${params.operationName || 'unknown'}] ${params.message}${
          params.clientRequestId ? ` [clientRequestId=${params.clientRequestId}]` : ''
        }`,
      ),
    );

    rum.recordEvent?.('app_error', errorAttributes);
  } catch (error) {
    console.error('RUM recordError failed', error);
  }
};

export function reportGraphQLErrorsToRum(params: {
  source: RumErrorSourceType;
  errors: readonly NormalizedGraphQLError[];
  operationName?: string;
  clientRequestId?: string;
  variables?: unknown;
  opType?: string;
  httpStatus?: string;
  rawErrorData?: unknown;
}): void {
  const { source, errors, operationName, clientRequestId, variables, opType, httpStatus, rawErrorData } = params;

  for (const errorItem of errors) {
    reportRumError({
      source,
      operationName,
      message: errorItem.rawMessage || 'Unknown GraphQL error',
      clientRequestId,
      variables,
      extra: {
        opType: opType || '',
        graphQLErrorCode: errorItem.code,
        httpStatus: httpStatus || '',
        rawErrorData,
        rawTitle: errorItem.rawTitle,
        isSystemGraphQLError: errorItem.isSystemGraphQLError,
      },
    });
  }
}
